import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CategoryDonut from "@/components/CategoryDonut";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShoppingBag, Receipt, Wallet, FileDown } from "lucide-react";

function isoDay(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

const CATEGORIES = [
  "Vegetables",
  "Meat & Poultry",
  "Groceries",
  "Dairy",
  "Beverages",
  "Cleaning",
  "Utilities",
  "Rent",
  "Salaries",
  "Repairs & Maintenance",
  "Equipment",
  "Marketing",
  "Transport",
  "Miscellaneous",
];

const PAYMENT_METHODS = ["Cash", "UPI", "Card", "Bank Transfer", "Cheque"];

const emptyForm = {
  entry_type: "purchase",
  amount: "",
  category: "Vegetables",
  description: "",
  manager: "",
  date: isoDay(0),
  payment_method: "Cash",
  vendor: "",
};

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function fmtINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export default function ExpensesPage() {
  const { user } = useAuth();
  const isManager = Boolean(user?.manager_name);
  const [rows, setRows] = useState([]);
  const [managers, setManagers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [start, setStart] = useState(isoDay(-29));
  const [end, setEnd] = useState(isoDay(0));
  const [filterManager, setFilterManager] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [pdfMonth, setPdfMonth] = useState(currentMonthValue());
  const [pdfBusy, setPdfBusy] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_date: start, end_date: end };
      if (filterManager !== "all") params.manager = filterManager;
      if (filterType !== "all") params.entry_type = filterType;
      const [{ data: list }, { data: sum }] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/expenses/summary", { params: { start_date: start, end_date: end } }),
      ]);
      setRows(list);
      setSummary(sum);
    } finally {
      setLoading(false);
    }
  }, [start, end, filterManager, filterType]);

  useEffect(() => {
    api.get("/expenses/managers").then(({ data }) => {
      setManagers(data.managers);
      setForm((f) => ({ ...f, manager: data.managers[0] || "" }));
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    // If the user is a manager, force attribution to themselves
    const managerToUse = isManager ? user.manager_name : (managers[0] || "");
    setForm({ ...emptyForm, manager: managerToUse });
    setOpen(true);
  };

  const downloadPdf = async () => {
    if (!pdfMonth) return;
    const [y, m] = pdfMonth.split("-").map((v) => parseInt(v, 10));
    setPdfBusy(true);
    try {
      const res = await api.get("/expenses/monthly-pdf", {
        params: { year: y, month: m },
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-ledger-${pdfMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${pdfMonth} ledger PDF`);
    } catch (err) {
      toast.error("Failed to generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      entry_type: row.entry_type,
      amount: String(row.amount),
      category: row.category || "General",
      description: row.description || "",
      manager: row.manager,
      date: row.date,
      payment_method: row.payment_method || "Cash",
      vendor: row.vendor || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.manager) {
      toast.error("Please select a manager");
      return;
    }
    setSaving(true);
    try {
      const body = { ...form, amount: parseFloat(form.amount) };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, body);
        toast.success("Entry updated");
      } else {
        await api.post("/expenses", body);
        toast.success(`${body.entry_type === "purchase" ? "Purchase" : "Expense"} recorded`);
      }
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/expenses/${toDelete.id}`);
      toast.success("Entry removed");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const managerBadges = useMemo(() => {
    if (!summary?.by_manager) return [];
    return summary.by_manager;
  }, [summary]);

  return (
    <div data-testid="expenses-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Finance</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Purchase & expense</h1>
          <p className="mt-3 text-muted-foreground">
            Every entry is signed by the manager who made it. Owner sees the full ledger.
          </p>
        </div>
        <Button
          onClick={openNew}
          data-testid="add-expense-btn"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> New entry
        </Button>
      </div>

      {isManager && (
        <div className="mt-6 bg-accent/20 border border-accent/40 px-4 py-3 rounded-md fade-up text-sm" data-testid="manager-badge">
          Signed in as <strong>{user.manager_name}</strong> — every entry you add is automatically attributed to you.
        </div>
      )}

      {/* Monthly ledger PDF panel */}
      <div className="mt-8 bg-white border border-border p-6 fade-up flex flex-col md:flex-row md:items-end gap-4" data-testid="pdf-panel">
        <div className="md:flex-1">
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Monthly ledger</p>
          <h2 className="mt-2 font-serif text-2xl tracking-tight">Download PDF report</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            One printable PDF with every purchase &amp; expense of the month, totals per manager, and a per-category breakdown.
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Month</Label>
          <Input
            type="month"
            data-testid="pdf-month-picker"
            value={pdfMonth}
            onChange={(e) => setPdfMonth(e.target.value)}
            className="mt-2"
          />
        </div>
        <Button
          onClick={downloadPdf}
          disabled={pdfBusy || !pdfMonth}
          data-testid="download-pdf-btn"
          className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
        >
          <FileDown className="w-4 h-4 mr-2" aria-hidden="true" />
          {pdfBusy ? "Generating…" : "Download month PDF"}
        </Button>
      </div>

      {/* Summary stats */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-border p-6 fade-up" data-testid="stat-purchase">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Purchase</div>
              <div className="mt-3 font-serif text-4xl tracking-tight">
                {fmtINR(summary?.totals?.purchase)}
              </div>
            </div>
            <ShoppingBag className="w-5 h-5 text-primary" aria-hidden="true" />
          </div>
        </div>
        <div className="bg-white border border-border p-6 fade-up" data-testid="stat-expense">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Expense</div>
              <div className="mt-3 font-serif text-4xl tracking-tight">
                {fmtINR(summary?.totals?.expense)}
              </div>
            </div>
            <Receipt className="w-5 h-5 text-primary" aria-hidden="true" />
          </div>
        </div>
        <div className="bg-primary text-primary-foreground p-6 fade-up" data-testid="stat-grand-total">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest opacity-70">Grand total</div>
              <div className="mt-3 font-serif text-4xl tracking-tight">
                {fmtINR(summary?.totals?.grand_total)}
              </div>
              <div className="mt-1 text-xs opacity-70">{summary?.totals?.count || 0} entries in range</div>
            </div>
            <Wallet className="w-5 h-5 opacity-90" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* Per-manager */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        {managerBadges.map((m) => (
          <div
            key={m.manager}
            className="bg-white border border-border p-6 fade-up"
            data-testid={`mgr-stat-${m.manager.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <div className="text-xs uppercase tracking-widest text-muted-foreground">By manager</div>
            <div className="mt-1 font-serif text-xl">{m.manager}</div>
            <div className="mt-4 flex items-baseline gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Purchase</div>
                <div className="font-serif text-lg">{fmtINR(m.purchase)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Expense</div>
                <div className="font-serif text-lg">{fmtINR(m.expense)}</div>
              </div>
              <div className="ml-auto text-xs text-muted-foreground">{m.count} entries</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="mt-8 bg-white border border-border p-6 fade-up" data-testid="category-donut-panel">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Spend by category</p>
            <h2 className="mt-1 font-serif text-2xl tracking-tight">Where the money went</h2>
          </div>
          <span className="text-xs text-muted-foreground hidden sm:block">
            Purchase + Expense · {start} to {end}
          </span>
        </div>
        <CategoryDonut data={summary?.by_category || []} size={220} testId="category-donut" />
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 bg-white border border-border p-6 fade-up">
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
          <Input type="date" data-testid="exp-filter-start" value={start} onChange={(e) => setStart(e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
          <Input type="date" data-testid="exp-filter-end" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Manager</Label>
          <Select value={filterManager} onValueChange={setFilterManager}>
            <SelectTrigger className="mt-2" data-testid="exp-filter-manager">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All managers</SelectItem>
              {managers.map((m) => (
                <SelectItem key={m} value={m}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type</Label>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="mt-2" data-testid="exp-filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="purchase">Purchase</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="mt-6 bg-white border border-border fade-up overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No entries in this range.</p>
            <p className="text-muted-foreground mt-2">Tap "New entry" to record a purchase or expense.</p>
          </div>
        ) : (
          <table className="w-full min-w-[720px]" data-testid="expenses-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-6 py-4 font-normal">Date</th>
                <th className="px-6 py-4 font-normal">Type</th>
                <th className="px-6 py-4 font-normal">Category</th>
                <th className="px-6 py-4 font-normal">Description</th>
                <th className="px-6 py-4 font-normal">Manager</th>
                <th className="px-6 py-4 font-normal">Method</th>
                <th className="px-6 py-4 font-normal text-right">Amount</th>
                <th className="px-6 py-4 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border" data-testid={`expense-row-${r.id}`}>
                  <td className="px-6 py-4 text-muted-foreground whitespace-nowrap">{r.date}</td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full ${
                        r.entry_type === "purchase"
                          ? "bg-primary/10 text-primary"
                          : "bg-secondary text-foreground"
                      }`}
                    >
                      {r.entry_type}
                    </span>
                  </td>
                  <td className="px-6 py-4">{r.category}</td>
                  <td className="px-6 py-4 text-muted-foreground max-w-xs truncate">
                    {r.description || "—"}
                    {r.vendor && <span className="ml-2 text-xs">· {r.vendor}</span>}
                  </td>
                  <td className="px-6 py-4 font-medium">{r.manager}</td>
                  <td className="px-6 py-4 text-muted-foreground">{r.payment_method}</td>
                  <td className="px-6 py-4 text-right font-serif text-lg">{fmtINR(r.amount)}</td>
                  <td className="px-6 py-4 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-expense-${r.id}`}>
                      <Pencil className="w-4 h-4" aria-hidden="true" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setToDelete(r)} data-testid={`delete-expense-${r.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="expense-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {editing ? "Edit entry" : "New purchase / expense"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select
                  value={form.entry_type}
                  onValueChange={(v) => setForm({ ...form, entry_type: v })}
                >
                  <SelectTrigger data-testid="form-entry-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchase">Purchase</SelectItem>
                    <SelectItem value="expense">Expense</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="amount">Amount (₹)</Label>
                <Input
                  id="amount"
                  data-testid="form-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="vendor">Vendor / Payee</Label>
                <Input
                  id="vendor"
                  data-testid="form-vendor"
                  value={form.vendor}
                  onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                  placeholder="e.g. Local Sabzi Mandi"
                />
              </div>
            </div>

            <div>
              <Label>Manager (who is making this entry)</Label>
              {isManager ? (
                <Input
                  value={user.manager_name}
                  disabled
                  data-testid="form-manager-readonly"
                  className="mt-1 bg-secondary"
                />
              ) : (
                <Select value={form.manager} onValueChange={(v) => setForm({ ...form, manager: v })}>
                  <SelectTrigger data-testid="form-manager">
                    <SelectValue placeholder="Select manager" />
                  </SelectTrigger>
                  <SelectContent>
                    {managers.map((m) => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  data-testid="form-date"
                  type="date"
                  required
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div>
                <Label>Payment method</Label>
                <Select
                  value={form.payment_method}
                  onValueChange={(v) => setForm({ ...form, payment_method: v })}
                >
                  <SelectTrigger data-testid="form-payment">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description / notes</Label>
              <Textarea
                id="description"
                data-testid="form-description"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. 25 kg tomato, 10 kg onion"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-expense-btn"
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Record entry"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif tracking-tight">Remove this entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes {toDelete?.entry_type} of {fmtINR(toDelete?.amount)} by {toDelete?.manager} on {toDelete?.date}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-expense-btn" onClick={doDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
